import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem,
  Popover, Snackbar, Stack, Tab, Tabs, TextField, Tooltip, Typography, Checkbox, FormControlLabel,
} from "@mui/material";
import {
  Eye, EyeOff, MoreHorizontal, Copy, MoveRight, Trash2, Link2, Paperclip, Plus, Play, Square, Sparkles, Clock, MessageSquare, History, Timer, X, Lock, CornerDownRight, ExternalLink, Archive, Pencil, Share2, ChevronDown,
} from "lucide-react";
import { GlassCard, PersonAvatar, T, TYPE, formatDateTime, formatRelative, primaryButtonSx, ghostButtonSx, labelSx } from "../user/complaints/shared";
import { useRoom, useSocketEvent } from "../helpdesk/socket";
import { fileSize } from "../helpdesk/ui";
import { pm, errorMessage, openFile, fileViewUrl } from "./api";
import { usePm } from "./PmContext";
import RichEditor, { RichView } from "./RichEditor";
import { TypeIcon, PriorityIcon, StatusPill, Assignee, IssueKey, Progress, minutesText, ACTIVITY_TEXT, CATEGORY_TONE } from "./ui";
import { CustomFieldInput, LabelsInput, MultiSelect, PersonPicker, PrioritySelect, Select, TypeSelect, applicableFields, dateInput, fieldSx } from "./inputs";
import CreateIssueDialog from "./CreateIssueDialog";
import SupportTickets from "./SupportTickets";

const EMOJIS = ["👍", "🎉", "❤️", "😄", "👀", "🚀", "✅", "❓"];

const SideRow = ({ label, children }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: "118px 1fr", alignItems: "center", gap: 1, py: 0.75 }}>
    <Typography sx={{ ...labelSx, fontSize: "0.7rem" }}>{label}</Typography>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

/** Jira-style status button with dropdown transitions. */
const StatusDropdown = ({ issue, canTransition, onTransition, archived }) => {
  const { statusById } = usePm();
  const currentStatus = statusById.get(String(issue.statusId));
  const [anchor, setAnchor] = useState(null);

  const category = currentStatus?.category || issue.statusCategory || "todo";
  const toneBg = category === "done" ? "#EDFDF5" : category === "in_progress" ? "#EFF8FF" : "#F4F5F7";
  const toneText = category === "done" ? "#027A48" : category === "in_progress" ? "#175CD3" : "#42526E";
  const toneBorder = category === "done" ? "#A6F4C5" : category === "in_progress" ? "#B2DDFF" : "#DFE1E6";

  const availableTransitions = issue.transitions || [];
  const interactive = canTransition && !archived && availableTransitions.length > 0;

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
      <Button
        size="small"
        onClick={(e) => interactive && setAnchor(e.currentTarget)}
        endIcon={interactive ? <ChevronDown size={14} /> : null}
        sx={{
          bgcolor: toneBg,
          color: toneText,
          border: `1px solid ${toneBorder}`,
          fontWeight: 700,
          fontSize: "0.78rem",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          px: 1.5,
          py: 0.6,
          borderRadius: "6px",
          cursor: interactive ? "pointer" : "default",
          boxShadow: "none",
          "&:hover": {
            bgcolor: toneBg,
            filter: interactive ? "brightness(0.95)" : "none",
            boxShadow: "none",
          },
        }}
      >
        {currentStatus?.name || "Status"}
      </Button>
      {issue.resolution && (
        <Chip size="small" label={`Resolution: ${issue.resolution}`} sx={{ height: 24, fontSize: TYPE.micro, bgcolor: "#F8F9FA", color: T.inkSecondary, border: `1px solid ${T.border}` }} />
      )}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{
          sx: {
            mt: 0.5,
            borderRadius: "8px",
            boxShadow: "0 10px 28px rgba(9, 30, 66, 0.18), 0 0 1px rgba(9, 30, 66, 0.25)",
            border: "1px solid #DFE1E6",
            minWidth: 190,
          },
        }}
      >
        <Typography sx={{ px: 2, py: 0.75, fontSize: "0.68rem", fontWeight: 700, color: T.inkMuted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Workflow transitions
        </Typography>
        {availableTransitions.map((t) => (
          <MenuItem
            key={t._id}
            disabled={Boolean(t.blockedBy)}
            onClick={() => {
              setAnchor(null);
              onTransition(t);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              py: 1,
              px: 2,
              fontSize: TYPE.small,
              fontWeight: 600,
              borderLeft: `4px solid ${CATEGORY_TONE[t.toCategory] || T.border}`,
              "&:hover": { bgcolor: T.surfaceMuted },
            }}
          >
            <Box sx={{ flexGrow: 1 }}>{t.name}</Box>
            {t.blockedBy && (
              <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>
                {t.blockedBy === "assignee_only" ? "(Assignee only)" : "(Blocked)"}
              </Typography>
            )}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};

/** Inline status change for a sub-task row: loads its workflow transitions on click. */
const SubtaskStatusCell = ({ sub, canTransition, ctx, onOpen, onDone }) => {
  const [anchor, setAnchor] = useState(null);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const openMenu = async (e) => {
    e.stopPropagation();
    if (!canTransition) return;
    const el = e.currentTarget;
    setAnchor(el);
    setItems(null);
    try {
      setItems(await pm.transitions(sub._id));
    } catch (err) {
      setAnchor(null);
      ctx.fail(err, "Could not load statuses");
    }
  };

  const pick = async (t) => {
    setAnchor(null);
    setBusy(true);
    try {
      await pm.transition(sub._id, { transitionId: t._id });
      onDone();
    } catch (err) {
      const res = err?.response?.data;
      if (err?.response?.status === 422 && res?.code === "TRANSITION_REQUIREMENTS") {
        ctx.notify(`${sub.key} needs more details for this change — opening it`, "warning");
        onOpen(sub.key);
      } else ctx.fail(err, "Transition failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <Box
        onClick={openMenu}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, cursor: canTransition ? "pointer" : "default", opacity: busy ? 0.5 : 1, borderRadius: "6px", "&:hover": canTransition ? { bgcolor: T.surfaceMuted } : undefined }}
      >
        <StatusPill statusId={sub.statusId} />
        {canTransition && <ChevronDown size={12} color={T.inkMuted} />}
      </Box>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} onClick={(e) => e.stopPropagation()}>
        {items === null && <Box sx={{ px: 2, py: 1 }}><CircularProgress size={16} /></Box>}
        {items?.length === 0 && <Typography sx={{ px: 2, py: 1, fontSize: TYPE.small, color: T.inkMuted }}>No transitions available</Typography>}
        {items?.map((t) => (
          <MenuItem
            key={t._id}
            disabled={Boolean(t.blockedBy)}
            onClick={() => pick(t)}
            sx={{ fontSize: TYPE.small, fontWeight: 600, borderLeft: `4px solid ${CATEGORY_TONE[t.toCategory] || T.border}` }}
          >
            {t.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

/** Inline priority change for a sub-task row. */
const SubtaskPriorityCell = ({ sub, canEdit, ctx, onDone }) => {
  const { priorities } = usePm();
  const [anchor, setAnchor] = useState(null);
  const [busy, setBusy] = useState(false);

  const pick = async (id) => {
    setAnchor(null);
    if (String(id) === String(sub.priorityId)) return;
    setBusy(true);
    try {
      await pm.updateIssue(sub._id, { priorityId: id });
      onDone();
    } catch (err) {
      ctx.fail(err, "Could not change priority");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <Box
        onClick={(e) => canEdit && setAnchor(e.currentTarget)}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, cursor: canEdit ? "pointer" : "default", opacity: busy ? 0.5 : 1, borderRadius: "6px", "&:hover": canEdit ? { bgcolor: T.surfaceMuted } : undefined }}
      >
        <PriorityIcon priorityId={sub.priorityId} size={14} />
        {canEdit && <ChevronDown size={12} color={T.inkMuted} />}
      </Box>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} onClick={(e) => e.stopPropagation()}>
        {priorities.filter((p) => p.isActive).map((p) => (
          <MenuItem key={p._id} selected={String(p._id) === String(sub.priorityId)} onClick={() => pick(p._id)} sx={{ fontSize: TYPE.small }}>
            <Box sx={{ mr: 1, display: "inline-flex" }}><PriorityIcon priorityId={p._id} size={14} /></Box>
            {p.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

/** Inline assignee change for a sub-task row. */
const SubtaskAssigneeCell = ({ sub, canAssign, ctx, onDone }) => {
  const [anchor, setAnchor] = useState(null);
  const [busy, setBusy] = useState(false);

  const assign = async (id) => {
    setAnchor(null);
    if (String(id || "") === String(sub.assignee?.id || "")) return;
    setBusy(true);
    try {
      await pm.updateIssue(sub._id, { assigneeId: id });
      onDone();
    } catch (err) {
      ctx.fail(err, "Could not assign");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box onClick={(e) => e.stopPropagation()} sx={{ minWidth: 0 }}>
      <Box
        onClick={(e) => canAssign && setAnchor(e.currentTarget)}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, maxWidth: "100%", cursor: canAssign ? "pointer" : "default", opacity: busy ? 0.5 : 1, borderRadius: "6px", "&:hover": canAssign ? { bgcolor: T.surfaceMuted } : undefined }}
      >
        <Assignee person={sub.assignee} size={20} showName />
        {canAssign && <ChevronDown size={12} color={T.inkMuted} />}
      </Box>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)} onClick={(e) => e.stopPropagation()} PaperProps={{ sx: { p: 1, width: 260 } }}>
        <PersonPicker label="" value={sub.assignee?.id} onChange={assign} />
      </Popover>
    </Box>
  );
};

/** Asks for whatever a transition's validators require (422 TRANSITION_REQUIREMENTS). */
const TransitionDialog = ({ state, onClose, onSubmit }) => {
  const ctx = usePm();
  const [input, setInput] = useState({ comment: "", resolution: "", fields: {} });
  useEffect(() => setInput({ comment: "", resolution: ctx.settings.resolutions[0] || "", fields: {} }), [state, ctx.settings.resolutions]);
  if (!state) return null;
  const v = state.validators || [];
  const needs = (type) => v.some((x) => x.type === type);
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{state.name}</DialogTitle>
      <DialogContent dividers>
        {state.message && <Alert severity="info" sx={{ mb: 2 }}>{state.message}</Alert>}
        <Stack spacing={2}>
          {needs("required_resolution") && (
            <Select label="Resolution" value={input.resolution} onChange={(r) => setInput((x) => ({ ...x, resolution: r }))} options={ctx.settings.resolutions.map((r) => ({ value: r, label: r }))} />
          )}
          {needs("assignee_required") && <PersonPicker value={input.fields.assigneeId} onChange={(id) => setInput((x) => ({ ...x, fields: { ...x.fields, assigneeId: id } }))} allowEmpty={false} />}
          {v.filter((x) => x.type === "required_field").map((x) => (
            <TextField key={x.field} size="small" label={x.message || x.field} value={input.fields[x.field] || ""} onChange={(e) => setInput((s) => ({ ...s, fields: { ...s.fields, [x.field]: e.target.value } }))} sx={fieldSx} />
          ))}
          {needs("required_attachment") && <Alert severity="warning">Attach at least one file to the issue first.</Alert>}
          <TextField multiline minRows={3} size="small" label={needs("required_comment") ? "Comment *" : "Comment (optional)"} value={input.comment} onChange={(e) => setInput((x) => ({ ...x, comment: e.target.value }))} sx={fieldSx} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button sx={ghostButtonSx} variant="outlined" onClick={onClose}>Cancel</Button>
        <Button sx={primaryButtonSx} variant="contained" onClick={() => onSubmit(input)}>{state.name}</Button>
      </DialogActions>
    </Dialog>
  );
};

const LinkDialog = ({ open, onClose, issue, onSaved }) => {
  const ctx = usePm();
  const [linkTypeId, setType] = useState("");
  const [direction, setDirection] = useState("outward");
  const [targetKey, setTarget] = useState("");
  const [options, setOptions] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setType(ctx.linkTypes.find((l) => l.isActive)?._id || "");
      setTarget("");
      setError("");
    }
  }, [open, ctx.linkTypes]);
  useEffect(() => {
    if (!open || targetKey.length < 2) return undefined;
    const t = setTimeout(() => pm.searchIssues({ text: targetKey }, { limit: 10 }).then((r) => setOptions(r.data.filter((i) => i._id !== issue._id))).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [targetKey, open, issue._id]);
  const choices = ctx.linkTypes.filter((l) => l.isActive).flatMap((l) => [{ value: `${l._id}|outward`, label: l.outward }, ...(l.inward !== l.outward ? [{ value: `${l._id}|inward`, label: l.inward }] : [])]);
  const save = async () => {
    try {
      await pm.addLink(issue._id, { linkTypeId, direction, targetKey: targetKey.trim().toUpperCase() });
      onSaved();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Link issue</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2}>
          <Select label={`${issue.key} …`} value={`${linkTypeId}|${direction}`} onChange={(v) => { const [a, b] = v.split("|"); setType(a); setDirection(b); }} options={choices} />
          <TextField size="small" label="Issue key or text" value={targetKey} onChange={(e) => setTarget(e.target.value)} sx={fieldSx} />
          {options.length > 0 && (
            <Stack spacing={0.5}>
              {options.map((o) => (
                <Box key={o._id} onClick={() => setTarget(o.key)} sx={{ display: "flex", gap: 1, alignItems: "center", p: 0.75, borderRadius: "6px", cursor: "pointer", bgcolor: targetKey.toUpperCase() === o.key ? T.brandSoft : "transparent", "&:hover": { bgcolor: T.surfaceMuted } }}>
                  <TypeIcon typeId={o.typeId} size={14} />
                  <Typography sx={{ fontSize: TYPE.small, fontWeight: 600 }}>{o.key}</Typography>
                  <Typography noWrap sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>{o.summary}</Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button sx={ghostButtonSx} variant="outlined" onClick={onClose}>Cancel</Button>
        <Button sx={primaryButtonSx} variant="contained" disabled={!linkTypeId || !targetKey} onClick={save}>Link</Button>
      </DialogActions>
    </Dialog>
  );
};

const MoveDialog = ({ open, onClose, issue, onMoved }) => {
  const [projects, setProjects] = useState([]);
  const [target, setTarget] = useState(null);
  const [typeId, setTypeId] = useState(null);
  const [error, setError] = useState("");
  const targetProject = projects.find((p) => p._id === target);
  useEffect(() => {
    if (open) pm.projects().then((r) => setProjects(r.filter((p) => p._id !== String(issue.projectId)))).catch(() => {});
  }, [open, issue.projectId]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Move {issue.key}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2}>
          <Select label="Target project" value={target} onChange={setTarget} options={projects.map((p) => ({ value: p._id, label: `${p.key} · ${p.name}` }))} />
          {targetProject && <TypeSelect value={typeId || issue.typeId} onChange={setTypeId} typeIds={targetProject.issueTypeIds} />}
          <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>The issue gets a new key; the old key keeps working as a redirect. Sub-tasks move with it.</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button sx={ghostButtonSx} variant="outlined" onClick={onClose}>Cancel</Button>
        <Button sx={primaryButtonSx} variant="contained" disabled={!target} onClick={async () => {
          try {
            const moved = await pm.moveIssue(issue._id, { projectId: target, typeId: typeId || undefined });
            onMoved(moved);
          } catch (err) {
            setError(errorMessage(err));
          }
        }}>Move</Button>
      </DialogActions>
    </Dialog>
  );
};

const AiPanel = ({ issue, onApplySubtasks, onApplyDescription }) => {
  const ctx = usePm();
  const [mode, setMode] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (m) => {
    setMode(m);
    setBusy(true);
    setError("");
    setResult(null);
    try {
      setResult((await pm.issueAi(issue._id, m)).generated);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  if (!ctx.settings.ai?.enabled) return null;
  const g = result || {};
  return (
    <GlassCard sx={{ p: 2, mt: 2, borderColor: "#E9D7FE" }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Sparkles size={16} color="#7A5AF8" />
        <Typography sx={{ fontWeight: 700, fontSize: TYPE.small }}>AI assistant</Typography>
        <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Suggestions only — nothing changes until you apply it.</Typography>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {[["summary", "Summarise"], ["comments", "Summarise comments"], ["subtasks", "Suggest sub-tasks"], ["acceptance", "Acceptance criteria"], ["description", "Improve description"]].map(([m, l]) => (
          <Chip key={m} label={l} size="small" onClick={() => run(m)} color={mode === m ? "secondary" : "default"} variant={mode === m ? "filled" : "outlined"} />
        ))}
      </Stack>
      {busy && <CircularProgress size={18} sx={{ mt: 1.5 }} />}
      {error && <Alert severity="warning" sx={{ mt: 1.5 }}>{error}</Alert>}
      {result && (
        <Box sx={{ mt: 1.5, fontSize: TYPE.small }}>
          {g.summary && <Typography sx={{ fontSize: TYPE.small, mb: 1 }}>{g.summary}</Typography>}
          {["decisions", "openQuestions", "nextSteps", "criteria"].filter((k) => g[k]?.length).map((k) => (
            <Box key={k} sx={{ mb: 1 }}>
              <Typography sx={{ ...labelSx, fontSize: "0.68rem" }}>{k.replace(/([A-Z])/g, " $1")}</Typography>
              <ul style={{ margin: "4px 0", paddingLeft: 18 }}>{g[k].map((x, i) => <li key={i}>{x}</li>)}</ul>
            </Box>
          ))}
          {g.subtasks?.length > 0 && (
            <>
              <ul style={{ margin: "4px 0", paddingLeft: 18 }}>{g.subtasks.map((s, i) => <li key={i}><b>{s.summary}</b>{s.description ? ` — ${s.description}` : ""}</li>)}</ul>
              <Button size="small" sx={primaryButtonSx} variant="contained" onClick={() => onApplySubtasks(g.subtasks)}>Create these sub-tasks</Button>
            </>
          )}
          {g.description && (
            <>
              <Typography sx={{ whiteSpace: "pre-wrap", fontSize: TYPE.small, p: 1, bgcolor: T.surfaceMuted, borderRadius: "6px" }}>{g.description}</Typography>
              <Button size="small" sx={{ ...primaryButtonSx, mt: 1 }} variant="contained" onClick={() => onApplyDescription(g.description)}>Use as description</Button>
            </>
          )}
          {mode === "acceptance" && g.criteria?.length > 0 && (
            <Button size="small" sx={primaryButtonSx} variant="contained" onClick={() => onApplyDescription(null, g.criteria)}>Append to description</Button>
          )}
        </Box>
      )}
    </GlassCard>
  );
};

const Comment = ({ c, issue, canComment, onReply, reload, me, canModerate }) => {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(c.body);
  const [anchor, setAnchor] = useState(null);
  const own = String(c.author?.id) === String(me.id);
  const save = async () => {
    await pm.editComment(issue._id, c._id, { body });
    setEditing(false);
    reload();
  };
  return (
    <Box sx={{ display: "flex", gap: 1.25, py: 1.25 }}>
      <PersonAvatar name={c.author?.name} size={30} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography sx={{ fontWeight: 700, fontSize: TYPE.small }}>{c.author?.name}</Typography>
          <Tooltip title={formatDateTime(c.createdAt)}><Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{formatRelative(c.createdAt)}{c.editedAt ? " · edited" : ""}</Typography></Tooltip>
          {c.internal && <Chip size="small" icon={<Lock size={11} />} label="Internal" sx={{ height: 20, fontSize: "0.66rem", bgcolor: "#FEF0C7", color: "#B54708" }} />}
        </Stack>
        {editing ? (
          <Box sx={{ mt: 1 }}>
            <RichEditor value={body} onChange={setBody} minHeight={70} autoFocus onSubmit={save} />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button size="small" variant="contained" sx={primaryButtonSx} onClick={save}>Save</Button>
              <Button size="small" onClick={() => { setEditing(false); setBody(c.body); }}>Cancel</Button>
            </Stack>
          </Box>
        ) : c.deletedAt ? (
          <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted, fontStyle: "italic", mt: 0.5 }}>Comment deleted</Typography>
        ) : (
          <Box sx={{ mt: 0.25 }}><RichView html={c.body} /></Box>
        )}
        {!c.deletedAt && (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
            {(c.reactions || []).filter((r) => r.userIds?.length).map((r) => (
              <Chip key={r.emoji} size="small" label={`${r.emoji} ${r.userIds.length}`} onClick={() => pm.react(issue._id, c._id, r.emoji).then(reload)}
                sx={{ height: 22, fontSize: "0.72rem", bgcolor: r.userIds.map(String).includes(String(me.id)) ? T.brandSoft : T.track }} />
            ))}
            <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}><Typography sx={{ fontSize: "0.8rem" }}>☺+</Typography></IconButton>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
              <Stack direction="row" sx={{ px: 1 }}>{EMOJIS.map((e) => <IconButton key={e} size="small" onClick={() => { setAnchor(null); pm.react(issue._id, c._id, e).then(reload); }}>{e}</IconButton>)}</Stack>
            </Menu>
            {canComment && !c.parentId && <Button size="small" onClick={() => onReply(c)} sx={{ fontSize: TYPE.micro, minWidth: 0 }}>Reply</Button>}
            {own && <Button size="small" onClick={() => setEditing(true)} sx={{ fontSize: TYPE.micro, minWidth: 0 }}>Edit</Button>}
            {(own || canModerate) && <Button size="small" color="error" onClick={() => window.confirm("Delete this comment?") && pm.deleteComment(issue._id, c._id).then(reload)} sx={{ fontSize: TYPE.micro, minWidth: 0 }}>Delete</Button>}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

const Comments = ({ issue, perms, me }) => {
  const ctx = usePm();
  const [rows, setRows] = useState([]);
  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const canComment = perms.includes("issue.comment");
  const canInternal = perms.includes("issue.view_internal");
  const reload = useCallback(() => pm.comments(issue._id, { limit: 200 }).then((r) => setRows(r.data)).catch(() => {}), [issue._id]);
  useEffect(() => {
    reload();
  }, [reload]);
  useSocketEvent("issue.commented", (p) => {
    if (String(p?.issueId) === String(issue._id)) reload();
  });
  const send = async () => {
    if (!body) return;
    setBusy(true);
    try {
      await pm.addComment(issue._id, { body, internal, parentId: replyTo?._id });
      setBody("");
      setReplyTo(null);
      reload();
    } catch (err) {
      ctx.fail(err, "Could not post the comment");
    } finally {
      setBusy(false);
    }
  };
  const top = rows.filter((c) => !c.parentId);
  const replies = (id) => rows.filter((c) => String(c.parentId) === String(id));
  return (
    <Box>
      {canComment && (
        <Box sx={{ mb: 2 }}>
          {replyTo && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
              <CornerDownRight size={14} />
              <Typography sx={{ fontSize: TYPE.micro }}>Replying to {replyTo.author?.name}</Typography>
              <IconButton size="small" onClick={() => setReplyTo(null)}><X size={13} /></IconButton>
            </Stack>
          )}
          <RichEditor value={body} onChange={setBody} minHeight={70} placeholder="Add a comment… (@ to mention, Ctrl+Enter to send)" onSubmit={send}
            onUpload={(files) => pm.uploadAttachments(issue._id, files)} mentionRoles={ctx.projectRoles} />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <Button size="small" variant="contained" sx={primaryButtonSx} disabled={busy || !body} onClick={send}>Comment</Button>
            {canInternal && <FormControlLabel control={<Checkbox size="small" checked={internal} onChange={(e) => setInternal(e.target.checked)} />} label={<Typography sx={{ fontSize: TYPE.small }}>Internal (hidden from client roles)</Typography>} />}
          </Stack>
        </Box>
      )}
      {!rows.length && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>No comments yet.</Typography>}
      {top.map((c) => (
        <Box key={c._id} sx={{ borderBottom: `1px solid ${T.border}` }}>
          <Comment c={c} issue={issue} canComment={canComment} onReply={setReplyTo} reload={reload} me={me} canModerate={perms.includes("project.manage_settings")} />
          <Box sx={{ pl: 5 }}>
            {replies(c._id).map((r) => <Comment key={r._id} c={r} issue={issue} canComment={false} onReply={() => {}} reload={reload} me={me} canModerate={perms.includes("project.manage_settings")} />)}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const HistoryTab = ({ issue }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    pm.history(issue._id).then(setRows).catch(() => setRows([]));
  }, [issue._id, issue.rev]);
  if (!rows) return <CircularProgress size={18} />;
  return (
    <Stack spacing={0}>
      {rows.map((a) => (
        <Stack key={a._id} direction="row" spacing={1.25} sx={{ py: 1, borderBottom: `1px solid ${T.border}` }} alignItems="flex-start">
          <PersonAvatar name={a.actor?.name} size={24} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: TYPE.small }}>{ACTIVITY_TEXT(a)}</Typography>
            {a.action === "issue.updated" && a.field !== "description" && a.from !== undefined && typeof a.from !== "object" && (
              <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{String(a.from ?? "—")} → {String(a.meta?.display ?? a.to ?? "—")}</Typography>
            )}
            <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{formatDateTime(a.createdAt)}</Typography>
          </Box>
        </Stack>
      ))}
    </Stack>
  );
};

/** yyyy-MM-ddTHH:mm in local time, for datetime-local inputs. */
const localInput = (d) => {
  const x = d ? new Date(d) : new Date();
  return new Date(x.getTime() - x.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const WorkTab = ({ issue, perms, onChange, me }) => {
  const ctx = usePm();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ duration: "", description: "", startedAt: localInput(), remaining: "" });
  const [edit, setEdit] = useState(null);
  const reload = useCallback(() => pm.worklogs(issue._id).then(setRows).catch(() => {}), [issue._id]);
  useEffect(() => {
    reload();
  }, [reload, issue.timeSpent]);
  // mirrors the API: issue.worklog in the project, and your own log unless you are a PM admin
  const canManage = (w) => perms.includes("issue.worklog") && (String(w.user?.id) === String(me.id) || ctx.me.isAdmin);
  const add = async () => {
    try {
      await pm.addWorklog(issue._id, { duration: form.duration, description: form.description, startedAt: new Date(form.startedAt).toISOString(), remaining: form.remaining || undefined });
      setForm((f) => ({ ...f, duration: "", description: "", remaining: "" }));
      reload();
      onChange();
    } catch (err) {
      ctx.fail(err);
    }
  };
  const saveEdit = async () => {
    try {
      await pm.editWorklog(issue._id, edit._id, { duration: edit.duration, description: edit.description, startedAt: new Date(edit.startedAt).toISOString() });
      setEdit(null);
      reload();
      onChange();
    } catch (err) {
      ctx.fail(err, "Could not update the work log");
    }
  };
  const remove = (w) => pm.deleteWorklog(issue._id, w._id).then(() => { reload(); onChange(); }).catch((err) => ctx.fail(err));
  return (
    <Box>
      {perms.includes("issue.worklog") && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "120px 200px 1fr 120px auto" }, gap: 1, mb: 2, alignItems: "center" }}>
          <TextField size="small" label="Time spent" placeholder="1h 30m" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} sx={fieldSx} />
          <TextField size="small" type="datetime-local" label="Started" InputLabelProps={{ shrink: true }} value={form.startedAt} onChange={(e) => setForm({ ...form, startedAt: e.target.value })} sx={fieldSx} />
          <TextField size="small" label="What did you work on?" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} sx={fieldSx} />
          <TextField size="small" label="Remaining" placeholder="auto" value={form.remaining} onChange={(e) => setForm({ ...form, remaining: e.target.value })} sx={fieldSx} />
          <Button variant="contained" sx={primaryButtonSx} disabled={!form.duration} onClick={add}>Log</Button>
        </Box>
      )}
      {!rows.length && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>No work logged yet.</Typography>}
      {rows.map((w) => edit?._id === w._id ? (
        <Box key={w._id} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "120px 200px 1fr auto auto" }, gap: 1, py: 1, alignItems: "center", borderBottom: `1px solid ${T.border}` }}>
          <TextField size="small" label="Time spent" placeholder="1h 30m" autoFocus value={edit.duration} onChange={(e) => setEdit({ ...edit, duration: e.target.value })} onKeyDown={(e) => e.key === "Enter" && edit.duration && saveEdit()} sx={fieldSx} />
          <TextField size="small" type="datetime-local" label="Started" InputLabelProps={{ shrink: true }} value={edit.startedAt} onChange={(e) => setEdit({ ...edit, startedAt: e.target.value })} sx={fieldSx} />
          <TextField size="small" label="What did you work on?" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} sx={fieldSx} />
          <Button variant="contained" sx={primaryButtonSx} disabled={!edit.duration || !edit.startedAt} onClick={saveEdit}>Save</Button>
          <Button sx={ghostButtonSx} onClick={() => setEdit(null)}>Cancel</Button>
        </Box>
      ) : (
        <Stack key={w._id} direction="row" spacing={1.25} alignItems="center" sx={{ py: 1, borderBottom: `1px solid ${T.border}` }}>
          <PersonAvatar name={w.user?.name} size={24} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: TYPE.small }}><b>{w.user?.name}</b> logged <b>{minutesText(w.minutes)}</b>{w.source === "timer" ? " (timer)" : ""}</Typography>
            {w.description && <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>{w.description}</Typography>}
            <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{formatDateTime(w.startedAt)}</Typography>
          </Box>
          {canManage(w) && (
            <>
              <Tooltip title="Edit"><IconButton size="small" onClick={() => setEdit({ _id: w._id, duration: minutesText(w.minutes), description: w.description || "", startedAt: localInput(w.startedAt) })}><Pencil size={14} /></IconButton></Tooltip>
              <Tooltip title="Delete"><IconButton size="small" onClick={() => remove(w)}><Trash2 size={14} /></IconButton></Tooltip>
            </>
          )}
        </Stack>
      ))}
    </Box>
  );
};

/**
 * Full issue view. Used as a page (/projects/:key/issues/:issueKey) and inside
 * a side panel from the board/backlog (`embedded`).
 */
const IssueDetail = ({ issueKey, embedded = false, inModal = false, onClose, onChanged, initialAction = null, onOpenIssue = null }) => {
  const ctx = usePm();
  const navigate = useNavigate();
  const [issue, setIssue] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(0);
  const [editDesc, setEditDesc] = useState(false);
  const [desc, setDesc] = useState("");
  const [summary, setSummary] = useState("");
  const [transitionState, setTransitionState] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [menu, setMenu] = useState(null);
  const [refs, setRefs] = useState({ sprints: [], versions: [], components: [], epics: [], labels: [] });
  const [conflict, setConflict] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileRef = useRef(null);
  const me = ctx.me;

  const load = useCallback(async () => {
    try {
      const d = await pm.issue(issueKey);
      setIssue(d);
      setSummary(d.summary);
      setDesc(d.description);
      setConflict(false);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load the issue"));
    }
  }, [issueKey]);
  useEffect(() => {
    load();
  }, [load]);

  const projectId = issue?.project?._id;
  // opened from a board quick action ("Link issue…", "Move to project…")
  const actionDone = useRef(false);
  useEffect(() => {
    if (!issue || actionDone.current || !initialAction) return;
    actionDone.current = true;
    if (initialAction === "link") setLinkOpen(true);
    if (initialAction === "move") setMoveOpen(true);
  }, [issue, initialAction]);
  useEffect(() => {
    if (!projectId) return;
    Promise.all([
      pm.sprints(projectId, { status: "active,future" }).catch(() => []), pm.versions(projectId).catch(() => []),
      pm.components(projectId).catch(() => []), pm.epics(projectId).catch(() => []), pm.labels(projectId).catch(() => []),
    ]).then(([sprints, versions, components, epics, labels]) => setRefs({ sprints, versions, components, epics, labels: labels.map((x) => x.name) }));
  }, [projectId]);

  useRoom("project", projectId);
  useSocketEvent(["issue.updated", "issue.assigned", "issue.status.changed"], (p) => {
    if (issue && String(p?._id) === String(issue._id) && (p.rev === undefined || p.rev > issue.rev)) {
      if (editDesc) setConflict(true);
      else load();
    }
  });

  const perms = issue?.permissions || [];
  const can = (p) => perms.includes(p);
  const archived = Boolean(issue?.project?.archivedAt);

  const handleNav = (key) => {
    if (onOpenIssue) onOpenIssue(key);
    else navigate(`/projects/${issue.project.key}/issues/${key}`);
  };

  const handleShare = () => {
    const url = `${window.location.origin}/projects/${issue.project.key}/issues/${issue.key}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => setCopied(true)).catch(() => setCopied(true));
    } else {
      setCopied(true);
    }
  };

  const save = async (patch) => {
    try {
      const updated = await pm.updateIssue(issue._id, { ...patch, rev: issue.rev });
      setIssue((i) => ({ ...i, ...updated, customFields: updated.customFields || i.customFields }));
      await load();
      onChanged?.();
      return true;
    } catch (err) {
      if (err?.response?.status === 409) {
        setConflict(true);
        ctx.notify("Someone else changed this issue — reloaded the latest version", "warning");
        await load();
      } else ctx.fail(err, "Could not save");
      return false;
    }
  };

  const doTransition = async (t, input = {}) => {
    try {
      await pm.transition(issue._id, { transitionId: t._id, ...input });
      setTransitionState(null);
      await load();
      onChanged?.();
    } catch (err) {
      const res = err?.response?.data;
      if (err?.response?.status === 422 && res?.code === "TRANSITION_REQUIREMENTS") {
        setTransitionState({ ...t, validators: res.details?.transition?.validators || t.validators, message: res.message, _id: t._id });
      } else ctx.fail(err, "Transition failed");
    }
  };
  const startTransition = (t) => {
    const needsInput = (t.validators || []).some((v) => ["required_comment", "required_resolution", "required_field", "assignee_required"].includes(v.type));
    if (needsInput) setTransitionState(t);
    else doTransition(t);
  };

  const uploadFiles = async (files) => {
    try {
      await pm.uploadAttachments(issue._id, files);
      load();
    } catch (err) {
      ctx.fail(err, "Upload failed");
    }
  };

  const fields = useMemo(() => (issue ? applicableFields(ctx.customFields, issue.projectId, issue.typeId) : []), [ctx.customFields, issue]);

  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!issue) return <Box sx={{ p: 6, textAlign: "center" }}><CircularProgress size={26} /></Box>;

  const type = ctx.typeById.get(String(issue.typeId));
  const editable = can("issue.update") && !archived;
  const timerHere = issue.timer && String(issue.timer.issueId) === String(issue._id);
  const est = issue.originalEstimate || 0;
  const spentPct = est ? Math.min(100, Math.round(((issue.timeSpent || 0) / Math.max(est, (issue.timeSpent || 0) + (issue.remainingEstimate || 0))) * 100)) : 0;

  const isModalMode = embedded || inModal;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, bgcolor: "#FFFFFF", overflow: "hidden" }}>
      {/* Jira Modal / View Header */}
      <Box
        sx={{
          px: { xs: 2, sm: 2.5, md: 3 },
          py: 1.25,
          borderBottom: `1px solid ${T.border}`,
          bgcolor: "#FFFFFF",
          position: "sticky",
          top: 0,
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 52,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, overflow: "hidden" }}>
          {issue.parent && (
            <>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                onClick={() => handleNav(issue.parent.key)}
                sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
              >
                <TypeIcon typeId={issue.parent.typeId} size={15} />
                <Typography sx={{ fontSize: TYPE.small, fontWeight: 600, color: T.inkSecondary }}>
                  {issue.parent.key}
                </Typography>
              </Stack>
              <Typography sx={{ color: T.inkMuted }}>/</Typography>
            </>
          )}
          {issue.epic && !issue.parent && (
            <>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                onClick={() => handleNav(issue.epic.key)}
                sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
              >
                <TypeIcon typeId={issue.epic.typeId} size={15} />
                <Typography sx={{ fontSize: TYPE.small, fontWeight: 600, color: T.inkSecondary }}>
                  {issue.epic.key}
                </Typography>
              </Stack>
              <Typography sx={{ color: T.inkMuted }}>/</Typography>
            </>
          )}
          <TypeIcon typeId={issue.typeId} size={16} />
          <Typography sx={{ fontSize: TYPE.small, fontWeight: 700, color: T.inkPrimary }}>
            {issue.key}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title={issue.watching ? "Stop watching" : "Watch"}>
            <IconButton size="small" onClick={() => pm.watch(issue._id, !issue.watching).then(load)}>
              {issue.watching ? <Eye size={17} color={T.brand} /> : <EyeOff size={17} />}
            </IconButton>
          </Tooltip>
          <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mr: 0.5 }}>
            {issue.watchers?.length || 0}
          </Typography>
          <Tooltip title="Share issue link">
            <IconButton size="small" onClick={handleShare}>
              <Share2 size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title="More actions">
            <IconButton size="small" onClick={(e) => setMenu(e.currentTarget)}>
              <MoreHorizontal size={17} />
            </IconButton>
          </Tooltip>
          {isModalMode && (
            <Tooltip title="Open full page">
              <IconButton size="small" component={RouterLink} to={`/projects/${issue.project.key}/issues/${issue.key}`}>
                <ExternalLink size={16} />
              </IconButton>
            </Tooltip>
          )}
          {isModalMode && onClose && (
            <Tooltip title="Close (Esc)">
              <IconButton size="small" onClick={onClose} sx={{ ml: 0.5 }}>
                <X size={18} />
              </IconButton>
            </Tooltip>
          )}
          <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
            {can("issue.clone") && can("issue.create") && <MenuItem onClick={async () => { setMenu(null); try { const c = await pm.cloneIssue(issue._id, {}); ctx.notify(`Cloned as ${c.key}`); navigate(`/projects/${issue.project.key}/issues/${c.key}`); } catch (err) { ctx.fail(err); } }}><Copy size={15} style={{ marginRight: 8 }} />Clone</MenuItem>}
            {can("issue.move") && <MenuItem onClick={() => { setMenu(null); setMoveOpen(true); }}><MoveRight size={15} style={{ marginRight: 8 }} />Move to project</MenuItem>}
            {can("issue.link") && <MenuItem onClick={() => { setMenu(null); setLinkOpen(true); }}><Link2 size={15} style={{ marginRight: 8 }} />Link issue</MenuItem>}
            {editable && <MenuItem onClick={async () => { setMenu(null); await pm.archiveIssue(issue._id); ctx.notify(`${issue.key} archived`); onChanged?.(); if (isModalMode) onClose?.(); else navigate(`/projects/${issue.project.key}/issues`); }}><Archive size={15} style={{ marginRight: 8 }} />Archive</MenuItem>}
            {can("issue.delete") && <MenuItem sx={{ color: "#B42318" }} onClick={async () => { setMenu(null); if (!window.confirm(`Delete ${issue.key}${issue.subtasks?.length ? " and its sub-tasks" : ""}? It can be restored by an admin.`)) return; await pm.deleteIssue(issue._id); ctx.notify(`${issue.key} deleted`); onChanged?.(); if (isModalMode) onClose?.(); else navigate(`/projects/${issue.project.key}/issues`); }}><Trash2 size={15} style={{ marginRight: 8 }} />Delete</MenuItem>}
          </Menu>
        </Stack>
      </Box>

      {/* Independently Scrollable Body */}
      <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 2, sm: 2.5, md: 3 } }}>
        {conflict && <Alert severity="warning" sx={{ mb: 1.5 }} action={<Button size="small" onClick={() => { setEditDesc(false); load(); }}>Reload</Button>}>This issue was changed by someone else.</Alert>}
        {archived && <Alert severity="info" sx={{ mb: 1.5 }}>This project is archived — read only.</Alert>}

        {/* 2-Column Responsive Layout matching Jira */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 330px", lg: "minmax(0, 1fr) 360px" }, gap: { xs: 2.5, md: 3.5 }, alignItems: "start" }}>
          {/* Left Column: Summary, Actions, Description, Subtasks, Activity */}
          <Box sx={{ minWidth: 0 }}>
            {editable ? (
              <TextField fullWidth variant="standard" value={summary} onChange={(e) => setSummary(e.target.value)}
                onBlur={() => summary.trim() && summary !== issue.summary && save({ summary })}
                onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                InputProps={{ disableUnderline: true, sx: { fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.01em", color: T.inkPrimary } }} multiline />
            ) : (
              <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, color: T.inkPrimary }}>{issue.summary}</Typography>
            )}

            {/* Quick action buttons under summary */}
            <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
              {can("issue.create") && !archived && type?.level !== 2 && (
                <Button size="small" variant="outlined" startIcon={<Plus size={14} />} onClick={() => setSubOpen(true)}
                  sx={{ ...ghostButtonSx, py: 0.35, px: 1.1, fontSize: "0.75rem", borderRadius: "6px" }}>
                  Add sub-task
                </Button>
              )}
              {can("issue.link") && !archived && (
                <Button size="small" variant="outlined" startIcon={<Link2 size={14} />} onClick={() => setLinkOpen(true)}
                  sx={{ ...ghostButtonSx, py: 0.35, px: 1.1, fontSize: "0.75rem", borderRadius: "6px" }}>
                  Link issue
                </Button>
              )}
              {can("issue.attach") && !archived && (
                <Button size="small" variant="outlined" startIcon={<Paperclip size={14} />} onClick={() => fileRef.current?.click()}
                  sx={{ ...ghostButtonSx, py: 0.35, px: 1.1, fontSize: "0.75rem", borderRadius: "6px" }}>
                  Attach
                </Button>
              )}
            </Stack>

            {/* description */}
            <Typography sx={{ ...labelSx, mb: 0.75 }}>Description</Typography>
            {editDesc ? (
              <Box>
                <RichEditor value={desc} onChange={setDesc} autoFocus minHeight={160} onUpload={(files) => pm.uploadAttachments(issue._id, files)} mentionRoles={ctx.projectRoles} onSubmit={async () => (await save({ description: desc })) && setEditDesc(false)} />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" variant="contained" sx={primaryButtonSx} onClick={async () => (await save({ description: desc })) && setEditDesc(false)}>Save</Button>
                  <Button size="small" onClick={() => { setDesc(issue.description); setEditDesc(false); }}>Cancel</Button>
                </Stack>
              </Box>
            ) : (
              <Box onClick={() => editable && setEditDesc(true)} sx={{ p: 1, mx: -1, borderRadius: "8px", cursor: editable ? "text" : "default", "&:hover": editable ? { bgcolor: T.surfaceMuted } : {} }}>
                <RichView html={issue.description} empty={editable ? "Add a description…" : "No description"} />
              </Box>
            )}

            <AiPanel issue={issue}
              onApplySubtasks={async (items) => {
                const sub = ctx.issueTypes.find((t) => t.level === 2 && (t.allowedParentTypeIds || []).map(String).includes(String(issue.typeId)));
                if (!sub) return ctx.notify("No sub-task type can sit under this issue type", "warning");
                try {
                  await pm.createBatch(issue.project._id, items.map((s) => ({ type: sub.key, typeId: sub._id, summary: s.summary, description: s.description, parentId: issue._id })));
                  ctx.notify(`${items.length} sub-tasks created`);
                  load();
                } catch (err) {
                  ctx.fail(err);
                }
              }}
              onApplyDescription={async (text, criteria) => {
                const esc = (s) => String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
                const html = criteria ? `${issue.description || ""}<h3>Acceptance criteria</h3><ul>${criteria.map((c) => `<li>${esc(c)}</li>`).join("")}</ul>` : text.split(/\n{2,}/).map((p) => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`).join("");
                await save({ description: html });
              }} />

            {/* sub-tasks */}
            {(type?.level !== 2) && (
              <Box sx={{ mt: 3 }}>
                <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ ...labelSx, flexGrow: 1 }}>{type?.level === 0 ? "Issues in this epic" : "Sub-tasks"}</Typography>
                  {issue.epicProgress && <Box sx={{ width: 160, mr: 1 }}><Progress value={issue.epicProgress.issues ? (issue.epicProgress.done / issue.epicProgress.issues) * 100 : 0} label={`${issue.epicProgress.done}/${issue.epicProgress.issues} done`} /></Box>}
                  {can("issue.create") && !archived && <Button size="small" startIcon={<Plus size={14} />} onClick={() => setSubOpen(true)}>Add</Button>}
                </Stack>
                <EpicChildren issue={issue} onOpenIssue={onOpenIssue} />
                {issue.subtasks?.length > 0 && (
                  <Box sx={{ border: `1px solid ${T.border}`, borderRadius: "8px", overflow: "hidden" }}>
                    {(() => {
                      const cols = "minmax(90px, auto) minmax(0, 1fr) 90px minmax(110px, 150px) 110px";
                      return (
                        <>
                          <Box sx={{ display: "grid", gridTemplateColumns: cols, columnGap: 1.5, alignItems: "center", px: 1.5, py: 0.75, bgcolor: T.surfaceMuted, borderBottom: `1px solid ${T.border}` }}>
                            {["Key", "Summary", "Priority", "Assignee", "Status"].map((h) => (
                              <Typography key={h} sx={{ ...labelSx, fontSize: "0.68rem" }}>{h}</Typography>
                            ))}
                          </Box>
                          {issue.subtasks.map((s, i) => (
                            <Box
                              key={s._id}
                              onClick={() => handleNav(s.key)}
                              sx={{
                                display: "grid", gridTemplateColumns: cols, columnGap: 1.5, alignItems: "center", px: 1.5, py: 1, cursor: "pointer",
                                borderBottom: i < issue.subtasks.length - 1 ? `1px solid ${T.border}` : "none",
                                "&:hover": { bgcolor: T.surfaceMuted },
                              }}
                            >
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <TypeIcon typeId={s.typeId} size={14} />
                                <IssueKey issue={s} projectKey={issue.project.key} onClick={() => handleNav(s.key)} />
                              </Stack>
                              <Typography noWrap title={s.summary} sx={{ fontSize: TYPE.small }}>{s.summary}</Typography>
                              <SubtaskPriorityCell sub={s} canEdit={editable} ctx={ctx} onDone={() => { load(); onChanged?.(); }} />
                              <SubtaskAssigneeCell sub={s} canAssign={can("issue.assign") && !archived} ctx={ctx} onDone={() => { load(); onChanged?.(); }} />
                              <SubtaskStatusCell sub={s} canTransition={can("issue.transition") && !archived} ctx={ctx} onOpen={handleNav} onDone={() => { load(); onChanged?.(); }} />
                            </Box>
                          ))}
                        </>
                      );
                    })()}
                  </Box>
                )}
              </Box>
            )}

            {/* links */}
            {(issue.links?.length > 0 || can("issue.link")) && (
              <Box sx={{ mt: 3 }}>
                <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ ...labelSx, flexGrow: 1 }}>Linked work items</Typography>
                  {can("issue.link") && !archived && <Button size="small" startIcon={<Link2 size={14} />} onClick={() => setLinkOpen(true)}>Link</Button>}
                </Stack>
                {Object.entries((issue.links || []).reduce((g, l) => ({ ...g, [l.label]: [...(g[l.label] || []), l] }), {})).map(([label, list]) => (
                  <Box key={label} sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary, fontWeight: 600, mb: 0.25 }}>{label}</Typography>
                    {list.map((l) => (
                      <Stack
                        key={l._id}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        onClick={() => handleNav(l.issue.key)}
                        sx={{ py: 0.5, px: 1, borderRadius: "6px", cursor: "pointer", "&:hover": { bgcolor: T.surfaceMuted } }}
                      >
                        <TypeIcon typeId={l.issue.typeId} size={14} />
                        <IssueKey issue={l.issue} onClick={() => handleNav(l.issue.key)} />
                        <Typography noWrap sx={{ fontSize: TYPE.small, flexGrow: 1 }}>{l.issue.summary}</Typography>
                        <StatusPill statusId={l.issue.statusId} />
                        {can("issue.link") && !archived && <IconButton size="small" onClick={(e) => { e.stopPropagation(); pm.removeLink(issue._id, l._id).then(load); }}><X size={13} /></IconButton>}
                      </Stack>
                    ))}
                  </Box>
                ))}
              </Box>
            )}

            <SupportTickets issueId={issue._id} canEdit={(can("issue.link") || can("issue.update")) && !archived} />

            {/* attachments */}
            <Box sx={{ mt: 3 }}
              onDragOver={(e) => { if (can("issue.attach")) e.preventDefault(); }}
              onDrop={(e) => { if (!can("issue.attach")) return; e.preventDefault(); uploadFiles([...e.dataTransfer.files]); }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Typography sx={{ ...labelSx, flexGrow: 1 }}>Attachments ({issue.attachments?.length || 0})</Typography>
                {can("issue.attach") && !archived && <Button size="small" startIcon={<Paperclip size={14} />} onClick={() => fileRef.current?.click()}>Attach</Button>}
                <input ref={fileRef} type="file" hidden multiple onChange={(e) => { uploadFiles([...e.target.files]); e.target.value = ""; }} />
              </Stack>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 1 }}>
                {issue.attachments?.map((f) => (
                  <GlassCard key={f._id} sx={{ p: 1, cursor: "pointer", boxShadow: "none" }} onClick={() => openFile(f)}>
                    {f.viewToken && f.mimeType.startsWith("image/") ? (
                      <Box component="img" src={fileViewUrl(f.viewToken)} alt={f.fileName} sx={{ width: "100%", height: 80, objectFit: "cover", borderRadius: "6px", bgcolor: T.track }} />
                    ) : (
                      <Box sx={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: T.track, borderRadius: "6px" }}><Paperclip size={22} color={T.inkMuted} /></Box>
                    )}
                    <Typography noWrap sx={{ fontSize: TYPE.micro, fontWeight: 600, mt: 0.5 }}>{f.fileName}</Typography>
                    <Typography sx={{ fontSize: "0.66rem", color: T.inkMuted }}>{fileSize(f.size)} · {f.uploadedBy?.name}</Typography>
                  </GlassCard>
                ))}
              </Box>
            </Box>

            {/* activity */}
            <Box sx={{ mt: 3 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36, borderBottom: `1px solid ${T.border}`, mb: 1.5, "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontSize: TYPE.small } }}>
                <Tab icon={<MessageSquare size={14} />} iconPosition="start" label={`Comments${issue.commentCount ? ` (${issue.commentCount})` : ""}`} />
                <Tab icon={<History size={14} />} iconPosition="start" label="History" />
                <Tab icon={<Timer size={14} />} iconPosition="start" label="Work log" />
              </Tabs>
              {tab === 0 && <Comments issue={issue} perms={archived ? perms.filter((p) => p.endsWith(".view") || p === "issue.view_internal") : perms} me={me} />}
              {tab === 1 && <HistoryTab issue={issue} />}
              {tab === 2 && <WorkTab issue={issue} perms={archived ? [] : perms} onChange={load} me={me} />}
            </Box>
          </Box>

          {/* Right Column: Properties Details Sidebar */}
          <Box>
            {/* Top Jira Status Dropdown */}
            <StatusDropdown issue={issue} canTransition={can("issue.transition")} onTransition={startTransition} archived={archived} />

            <GlassCard sx={{ p: 2, borderRadius: "10px", border: `1px solid #DFE1E6`, bgcolor: "#FFFFFF", boxShadow: "none" }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: T.inkPrimary, mb: 1, letterSpacing: "-0.01em" }}>
                Details
              </Typography>
              <SideRow label="Assignee">
                {can("issue.assign") || (editable && !issue.assignee) ? (
                  <PersonPicker label="" value={issue.assignee?.id} onChange={(id) => save({ assigneeId: id })} />
                ) : <Assignee person={issue.assignee} showName />}
                {editable && String(issue.assignee?.id) !== String(me.id) && <Button size="small" sx={{ fontSize: TYPE.micro, p: 0, mt: 0.25 }} onClick={() => save({ assigneeId: String(me.id) })}>Assign to me</Button>}
              </SideRow>
              <SideRow label="Reporter">
                {editable ? <PersonPicker label="" value={issue.reporter?.id} onChange={(id) => save({ reporterId: id })} allowEmpty={false} /> : <Assignee person={issue.reporter} showName />}
              </SideRow>
              <SideRow label="Priority">{editable ? <PrioritySelect label="" value={issue.priorityId} onChange={(id) => save({ priorityId: id })} /> : <PriorityIcon priorityId={issue.priorityId} withLabel />}</SideRow>
              <SideRow label="Type">{editable ? <TypeSelect label="" value={issue.typeId} onChange={(id) => save({ typeId: id })} level={type?.level === 0 ? 0 : type?.level === 2 ? 2 : 1} /> : type?.name}</SideRow>
              {issue.parent && (
                <SideRow label="Parent">
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    onClick={() => handleNav(issue.parent.key)}
                    sx={{ cursor: "pointer", py: 0.25, px: 0.5, borderRadius: "4px", "&:hover": { bgcolor: T.surfaceMuted } }}
                  >
                    <TypeIcon typeId={issue.parent.typeId} size={14} />
                    <Typography sx={{ fontSize: TYPE.small, fontWeight: 600, color: T.brand }}>{issue.parent.key}</Typography>
                    <Typography noWrap sx={{ fontSize: TYPE.small, color: T.inkSecondary, maxWidth: 160 }}>{issue.parent.summary}</Typography>
                  </Stack>
                </SideRow>
              )}
              {type?.level === 1 && (
                <SideRow label="Epic">
                  {editable ? <Select label="" value={issue.epicId} onChange={(id) => save({ parentId: id })} allowEmpty options={refs.epics.map((e) => ({ value: e._id, label: `${e.key} ${e.summary}` }))} /> : issue.epic ? <IssueKey issue={issue.epic} onClick={() => handleNav(issue.epic.key)} /> : "—"}
                </SideRow>
              )}
              {issue.project.sprintsEnabled !== false && type?.level !== 0 && (
                <SideRow label="Sprint">
                  {editable ? <Select label="" value={issue.sprintId} onChange={(id) => save({ sprintId: id })} allowEmpty emptyLabel="Backlog" options={[...refs.sprints, ...(issue.sprint && !refs.sprints.some((s) => String(s._id) === String(issue.sprintId)) ? [{ _id: issue.sprintId, name: issue.sprint.name }] : [])].map((s) => ({ value: s._id, label: s.name }))} /> : issue.sprint?.name || "Backlog"}
                </SideRow>
              )}
              <SideRow label="Story points">
                {editable ? <TextField size="small" type="number" defaultValue={issue.storyPoints ?? ""} key={`sp${issue.rev}`} onBlur={(e) => String(e.target.value) !== String(issue.storyPoints ?? "") && save({ storyPoints: e.target.value === "" ? null : Number(e.target.value) })} sx={{ ...fieldSx, width: 100 }} /> : issue.storyPoints ?? "—"}
              </SideRow>
              <SideRow label="Labels">{editable ? <LabelsInput label="" value={issue.labels} options={refs.labels} onChange={(labels) => save({ labels })} /> : issue.labels.join(", ") || "—"}</SideRow>
              <SideRow label="Components">{editable ? <MultiSelect label="" value={issue.componentIds} onChange={(ids) => save({ componentIds: ids })} options={refs.components.map((c) => ({ value: c._id, label: c.name }))} /> : "—"}</SideRow>
              <SideRow label="Fix versions">{editable ? <MultiSelect label="" value={issue.fixVersionIds} onChange={(ids) => save({ fixVersionIds: ids })} options={refs.versions.filter((v) => v.status !== "archived").map((v) => ({ value: v._id, label: v.name }))} /> : "—"}</SideRow>
              <SideRow label="Start date">{editable ? <TextField size="small" type="date" value={dateInput(issue.startDate)} onChange={(e) => save({ startDate: e.target.value || null })} sx={fieldSx} /> : dateInput(issue.startDate) || "—"}</SideRow>
              <SideRow label="Due date">{editable ? <TextField size="small" type="date" value={dateInput(issue.dueDate)} onChange={(e) => save({ dueDate: e.target.value || null })} sx={fieldSx} /> : dateInput(issue.dueDate) || "—"}</SideRow>
              {fields.map((f) => (
                <SideRow key={f._id} label={f.label}>
                  {editable ? (
                    <CustomFieldInput field={{ ...f, label: "" }} value={issue.customFields?.[f.key]} onChange={(v) => save({ customFields: { [f.key]: v } })} refs={{ teams: ctx.teams, sprints: refs.sprints, versions: refs.versions }} />
                  ) : String(issue.customFields?.[f.key] ?? "—")}
                </SideRow>
              ))}
            </GlassCard>

            <GlassCard sx={{ p: 2, mt: 2, borderRadius: "10px", border: `1px solid #DFE1E6`, bgcolor: "#FFFFFF", boxShadow: "none" }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Clock size={15} color={T.brand} />
                <Typography sx={{ fontWeight: 700, fontSize: TYPE.small, ml: 1, flexGrow: 1 }}>Time tracking</Typography>
                {can("issue.worklog") && !archived && (
                  timerHere ? (
                    <Button size="small" color="error" startIcon={<Square size={13} />} onClick={async () => { try { await pm.stopTimer({}); load(); } catch (err) { ctx.fail(err); } }}>Stop · {formatRelative(issue.timer.startedAt).replace(" ago", "")}</Button>
                  ) : (
                    <Button size="small" startIcon={<Play size={13} />} onClick={async () => { try { await pm.startTimer(issue._id); load(); } catch (err) { ctx.fail(err); } }}>Start timer</Button>
                  )
                )}
              </Stack>
              <Progress value={spentPct} color={issue.remainingEstimate === 0 && issue.statusCategory !== "done" ? "#F04438" : T.brand} />
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
                <Typography sx={{ fontSize: TYPE.micro }}><b>{minutesText(issue.timeSpent || 0)}</b> logged</Typography>
                <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>{minutesText(issue.remainingEstimate)} remaining</Typography>
              </Stack>
              {editable && (
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <TextField size="small" label="Original estimate" placeholder="2h" key={`oe${issue.rev}`} defaultValue={issue.originalEstimate ? minutesText(issue.originalEstimate) : ""} onBlur={(e) => e.target.value !== (issue.originalEstimate ? minutesText(issue.originalEstimate) : "") && save({ originalEstimate: e.target.value || null })} sx={fieldSx} />
                  <TextField size="small" label="Remaining" placeholder="1h" key={`re${issue.rev}`} defaultValue={issue.remainingEstimate !== null && issue.remainingEstimate !== undefined ? minutesText(issue.remainingEstimate) : ""} onBlur={(e) => e.target.value !== (issue.remainingEstimate != null ? minutesText(issue.remainingEstimate) : "") && save({ remainingEstimate: e.target.value || null })} sx={fieldSx} />
                </Stack>
              )}
            </GlassCard>

            <Box sx={{ mt: 1.5, px: 0.5 }}>
              <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Created {formatDateTime(issue.createdAt)} by {issue.createdBy?.name || issue.reporter?.name}</Typography>
              <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Updated {formatRelative(issue.updatedAt)}</Typography>
              {issue.resolvedAt && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Resolved {formatDateTime(issue.resolvedAt)}</Typography>}
              {issue.previousKeys?.length > 0 && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Previously {issue.previousKeys.join(", ")}</Typography>}
              {issue.external?.key && (
                <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>
                  Imported{issue.external.key !== issue.key ? ` · original key ${issue.external.key}` : ""}
                  {!issue.assignee && issue.external.assignee ? ` · original assignee: ${issue.external.assignee}` : ""}
                  {issue.external.reporter && issue.reporter?.name !== issue.external.reporter ? ` · original reporter: ${issue.external.reporter}` : ""}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <TransitionDialog state={transitionState} onClose={() => setTransitionState(null)} onSubmit={(input) => doTransition(transitionState, input)} />
      <LinkDialog open={linkOpen} onClose={() => setLinkOpen(false)} issue={issue} onSaved={load} />
      <MoveDialog open={moveOpen} onClose={() => setMoveOpen(false)} issue={issue} onMoved={(m) => { setMoveOpen(false); ctx.notify(`Moved to ${m.key}`); navigate(`/projects/${m.key.split("-")[0]}/issues/${m.key}`); }} />
      <CreateIssueDialog open={subOpen} onClose={() => setSubOpen(false)} project={issue.project._id ? { ...issue.project, issueTypeIds: issue.project.issueTypeIds || ctx.issueTypes.map((t) => t._id) } : null}
        defaults={type?.level === 0 ? { parentId: issue._id } : { parentId: issue._id, typeId: ctx.issueTypes.find((t) => t.level === 2 && (t.allowedParentTypeIds || []).map(String).includes(String(issue.typeId)))?._id }}
        onCreated={() => { setSubOpen(false); load(); }} />
      <Snackbar open={copied} autoHideDuration={3000} onClose={() => setCopied(false)} message="Issue link copied to clipboard" />
    </Box>
  );
};

/** Children of an epic (story/task/bug) — loaded on demand. */
const EpicChildren = ({ issue, onOpenIssue }) => {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    if (issue.typeLevel !== 0) return;
    pm.projectIssues(issue.projectId, { all: [{ field: "epic", op: "eq", value: String(issue._id) }] }, { limit: 200 }).then((r) => setRows(r.data)).catch(() => {});
  }, [issue._id, issue.typeLevel, issue.projectId, issue.rev]);
  if (issue.typeLevel !== 0) return null;
  return rows.map((s) => (
    <Stack key={s._id} direction="row" spacing={1} alignItems="center"
      onClick={() => onOpenIssue ? onOpenIssue(s.key) : navigate(`/projects/${s.key.split("-")[0]}/issues/${s.key}`)}
      sx={{ py: 0.75, px: 1, borderRadius: "6px", cursor: "pointer", "&:hover": { bgcolor: T.surfaceMuted } }}>
      <TypeIcon typeId={s.typeId} size={14} />
      <IssueKey issue={s} onClick={onOpenIssue ? () => onOpenIssue(s.key) : undefined} />
      <Typography noWrap sx={{ fontSize: TYPE.small, flexGrow: 1 }}>{s.summary}</Typography>
      <Assignee person={s.assignee} size={20} />
      <StatusPill statusId={s.statusId} />
    </Stack>
  ));
};

export { default as IssueDetailModal } from "./IssueDetailModal";
export default IssueDetail;
